import { QueryClient, type QueryKey, type UseQueryResult, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { hours, minutes, seconds } from "@/3514/utils"

import { ECoachGroupParticipantQueryKey as EKey } from "./coach.group-participant.api.config"
import adapters from "./coach.group-participant.api.queries.adapters"
import fetchers from "./coach.group-participant.api.queries.fetchers"
import type {
    ICoachGroupParticipantModuleByIdQueryDto as IDto,
    ICoachGroupParticipantModuleByIdQueryModel as IModel
} from "./coach.group-participant.api.types"

type TCoachParticipantModuleByIdQueryParams = { participantId: number; moduleId: number }
type TCoachParticipantModuleByIdQuery = UseQueryResult<IModel, AxiosError>
function useCoachParticipantModuleByIdQuery({
    participantId,
    moduleId
}: TCoachParticipantModuleByIdQueryParams): ReturnType<
    (params: TCoachParticipantModuleByIdQueryParams) => TCoachParticipantModuleByIdQuery
> {
    return useQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey.ModuleById({ participantId, moduleId }),
        queryFn: fetchers.moduleById,
        select: adapters.moduleById,
        staleTime: hours(0.5),
        gcTime: minutes(15),
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchInterval: false,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "online",
        notifyOnChangeProps: ["data", "isFetching", "isFetched"],
        enabled: true,
        throwOnError: false
    })
}

async function coachParticipantModuleByIdQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { participantId, moduleId }: { participantId: number; moduleId: number }
): Promise<void> {
    await qc.prefetchQuery<IDto, AxiosError, IModel, QueryKey>({
        queryKey: EKey.ModuleById({ participantId, moduleId }),
        queryFn: fetchers.moduleById,
        staleTime: hours(0.5),
        gcTime: minutes(15),
        retry: false
    })
}

export { useCoachParticipantModuleByIdQuery, coachParticipantModuleByIdQueryPrefetcher }
export type { TCoachParticipantModuleByIdQuery }
